import { Badge, Text } from '@mantine/core';
import type { MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { confidenceOf, difficultyColor, dimValue } from '@/shared/surfaces/InteractionRecordDrawer';
import { formatCount, formatTimestamp, humanUser } from '@/shared/analytics/MetricFormatting';

/**
 * The Use Cases prompt-grid columns: Category and Task are visible with
 * explicit enum filters so navigation context (a seeded category/subcategory
 * filter) is always editable in the grid — never degraded to a text filter.
 */
export const USE_CASE_PROMPT_COLUMNS: MegaGridColumn<InteractionRecord>[] = [
    {
        key: 'user',
        header: 'User',
        sortValue: (r) => humanUser(r.userId),
        render: (r) => (
            <Text size="xs" fw={600}>
                {humanUser(r.userId)}
            </Text>
        ),
    },
    {
        key: 'department',
        header: 'Department',
        sortValue: (r) => dimValue(r, 'user_department'),
        groupValue: (r) => dimValue(r, 'user_department'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'user_department') },
        render: (r) => (
            <Text size="xs" c="dimmed">
                {dimValue(r, 'user_department')}
            </Text>
        ),
    },
    {
        key: 'when',
        header: 'When',
        sortValue: (r) => new Date(r.timestamp),
        render: (r) => (
            <Text size="xs" c="dimmed" ff="monospace">
                {formatTimestamp(r.timestamp)}
            </Text>
        ),
    },
    {
        key: 'category',
        header: 'Category',
        sortValue: (r) => dimValue(r, 'task_category'),
        groupValue: (r) => dimValue(r, 'task_category'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'task_category') },
        render: (r) => <Text size="xs">{dimValue(r, 'task_category')}</Text>,
    },
    {
        key: 'task',
        header: 'Task',
        width: 220,
        sortValue: (r) => dimValue(r, 'task_subcategory'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'task_subcategory') },
        render: (r) => (
            <Text size="xs" c="dimmed" truncate>
                {dimValue(r, 'task_subcategory')}
            </Text>
        ),
    },
    {
        key: 'difficulty',
        header: 'Difficulty',
        sortValue: (r) => dimValue(r, 'difficulty'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'difficulty') },
        render: (r) => (
            <Badge size="xs" radius="sm" variant="light" color={difficultyColor(dimValue(r, 'difficulty'))}>
                {dimValue(r, 'difficulty')}
            </Badge>
        ),
    },
    {
        key: 'model',
        header: 'Model',
        defaultHidden: true,
        sortValue: (r) => r.model,
        filter: { type: 'enum', accessor: (r) => r.model },
        render: (r) => (
            <Text size="xs" ff="monospace">
                {r.model}
            </Text>
        ),
    },
    {
        key: 'out',
        header: 'Out',
        defaultHidden: true,
        align: 'right',
        sortValue: (r) => r.outputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {formatCount(r.outputTokens)}
            </Text>
        ),
    },
    {
        key: 'conf',
        header: 'Conf.',
        defaultHidden: true,
        align: 'right',
        sortValue: (r) => r.dimensions['task_category']?.confidence ?? null,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {confidenceOf(r, 'task_category')}
            </Text>
        ),
    },
];
